import { useCallback, useRef, useState } from "react";
import { View } from "react-native";
import native from "src/constants/native";
import useStyle from "src/hooks/useStyle";
import { color } from "src/styles";
import { radiusLarge } from "src/styles/radius";
import { paddingLarge, paddingMedium, paddingSmall, paddingXXL } from "src/styles/spacing";
import { TextInput, TextInputHandler } from "src/swsh-native";
import useCommentsThreadStore from "../../hooks/useCommentsThreadStore";
import usePostComment from "../../hooks/usePostComment";
import CommentSendButton from "./components/CommentSendButton";
import DisplayTagSuggestions from "./components/DisplayTagSuggestions";
import parseLikelySearchTerm from "./helpers/parseLikelySearchTerm";
import parseTextLinks from "./helpers/parseTextLinks";

const DefaultHeight = 48;
const LineHeight = 24;
const MaxLength = 500;

const CommentInputBar = () => {
	const { postComment } = usePostComment();
	const textInputRef = useRef<TextInputHandler | null>(null);
	const textRef = useRef<string | null>(null);
	const [disabled, setDisabled] = useState(true);
	const autoFillLookup = useRef<Record<string, string>>({});
	const handleSubmit = async () => {
		if (textRef.current === null) return;
		let curValue = textRef.current.trim();
		for (const [key, value] of Object.entries(autoFillLookup.current)) {
			curValue = curValue.replaceAll(key, `[${key}](${value})`);
		}
		setHeight(DefaultHeight);
		textInputRef.current?.clear();
		setDisabled(true);
		autoFillLookup.current = {};
		const parsedValue = parseTextLinks(curValue);
		await postComment(parsedValue);
	};
	const { theme } = useStyle();
	const [height, setHeight] = useState(DefaultHeight);
	// Adding minor artifical offset to make it look more normal
	const textPaddingVertical = (DefaultHeight - LineHeight - paddingSmall) / 2;
	const topic = useCommentsThreadStore((state) => state.topic);

	const threadIdentifier = useCommentsThreadStore((state) => state.threadSpecifier);
	const getUserTagSuggestions = useCommentsThreadStore((state) => state.getUserTagSuggestions);
	const isSuggesting = useRef(false);
	const [users, setUsers] = useState<
		{
			userId: string;
			firstName: string;
			lastName: string;
		}[]
	>([]);
	const handleSuggestText = useCallback(
		async (searchTerm: string) => {
			if (!getUserTagSuggestions) return;
			const users = await getUserTagSuggestions({
				...threadIdentifier,
				query: searchTerm,
				limit: 4,
			});
			if (!isSuggesting.current) return;
			setUsers(users);
		},
		[getUserTagSuggestions, threadIdentifier],
	);

	return (
		<View
			style={{
				flexDirection: "row",
				paddingHorizontal: paddingXXL,
				paddingVertical: paddingLarge,
				columnGap: paddingMedium,
				alignItems: "center",
			}}
		>
			<DisplayTagSuggestions
				users={users}
				onPress={({ firstName, lastName, userId }) => {
					const addText = `@${firstName} ${lastName}`;
					autoFillLookup.current[addText] = `/user_/${userId}`;
					const lastAtIndex = textRef.current?.lastIndexOf("@");
					if (lastAtIndex == null || lastAtIndex === -1) return;
					const newText = textRef.current?.slice(0, lastAtIndex) + addText + " ";
					textRef.current = newText;
					isSuggesting.current = false;
					setUsers([]);
					textInputRef.current?.setValue(newText);
					textInputRef.current?.focus();
				}}
			/>
			<TextInput
				testID="CommentInputBarTextInput"
				ref={textInputRef}
				maxLength={MaxLength}
				multiline
				onFocus={() => {
					topic.emit("onTextInputFocus", {});
				}}
				onContentSizeChange={(event) => {
					setHeight(
						Math.min(
							Math.max(
								native
									? // Native has different behavior for content size using the base text input instead of the padding
										event.nativeEvent.contentSize.height +
											2 * textPaddingVertical
									: event.nativeEvent.contentSize.height,
								DefaultHeight,
							),
							300,
						),
					);
				}}
				style={{
					backgroundColor: color.InputBackground[theme],
					paddingHorizontal: paddingLarge,
					lineHeight: LineHeight,
					paddingVertical: textPaddingVertical,
					borderRadius: radiusLarge,
					flex: 1,
					height,
				}}
				verticalAlign="middle"
				placeholder="Send a message"
				onChangeText={(text) => {
					textRef.current = text;
					if (text.charAt(text.length - 1) === "@" && !isSuggesting.current) {
						const searchTerm = parseLikelySearchTerm(text);
						if (searchTerm != null) {
							isSuggesting.current = true;
							handleSuggestText(searchTerm);
						}
					}
				}}
				onDebouncedChangeText={(text) => {
					if (text.length === 0) {
						setHeight(DefaultHeight);
						setDisabled(true);
					} else if (text.length > MaxLength) {
						setDisabled(true);
					} else {
						setDisabled(false);
					}
					if (isSuggesting.current) {
						const searchTerm = parseLikelySearchTerm(text);
						if (searchTerm != null) {
							handleSuggestText(searchTerm);
						} else {
							isSuggesting.current = false;
							setUsers([]);
						}
					}
				}}
				onSubmitEditing={handleSubmit}
			/>
			<CommentSendButton onPress={handleSubmit} disabled={disabled} size={DefaultHeight} />
		</View>
	);
};
export default CommentInputBar;
